import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Tabs, Tab, Row, Col, Card, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import { loadGoogleMaps } from '../helper/googleMapsLoader';
import { loadLeaflet } from '../helper/leafletLoader';
import { formatDisplayDate } from '../utils/formatDate';

const VenueMiniMap = ({ latitude, longitude, title }) => {
    const mapRef = useRef(null);
    const [mapLoaded, setMapLoaded] = useState(false);

    useEffect(() => {
        let isMounted = true;
        let leafletInstance = null;
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);

        if (isNaN(lat) || isNaN(lng)) return;

        const renderLeaflet = () => {
            loadLeaflet().then(L => {
                if (!isMounted || !mapRef.current) return;
                mapRef.current.innerHTML = '';
                const map = L.map(mapRef.current, {
                    center: [lat, lng],
                    zoom: 15,
                    zoomControl: false
                });
                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    maxZoom: 19
                }).addTo(map);
                leafletInstance = map;

                const pinIcon = L.divIcon({
                    html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;background:#0284c7;color:#fff;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.3);font-size:14px;">📍</div>`,
                    iconSize: [28, 28],
                    iconAnchor: [14, 28],
                    popupAnchor: [0, -28],
                    className: ''
                });

                L.marker([lat, lng], { icon: pinIcon })
                    .addTo(map)
                    .bindPopup(`<div style="font-family:sans-serif;font-size:12px;font-weight:600;">${title || 'Venue'}</div>`);

                if (isMounted) setMapLoaded(true);
            }).catch(err => console.error('Leaflet mini map load error:', err));
        };

        loadGoogleMaps()
            .then((google) => {
                if (!isMounted || !mapRef.current) return;
                try {
                    const pos = { lat, lng };
                    const map = new google.maps.Map(mapRef.current, {
                        center: pos,
                        zoom: 15,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                        zoomControl: true
                    });

                    const marker = new google.maps.Marker({
                        position: pos,
                        map,
                        title: title || 'Venue Location'
                    });

                    const info = new google.maps.InfoWindow({
                        content: `<div style="font-family:sans-serif;font-size:12px;font-weight:600;padding:2px;">${title || 'Venue'}</div>`
                    });

                    marker.addListener('click', () => info.open(map, marker));
                    if (isMounted) setMapLoaded(true);
                } catch (gErr) {
                    renderLeaflet();
                }
            })
            .catch(() => {
                renderLeaflet();
            });

        return () => {
            isMounted = false;
            if (leafletInstance && leafletInstance.remove) {
                try { leafletInstance.remove(); } catch (e) {}
            }
        };
    }, [latitude, longitude, title]);

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || isNaN(lng)) return null;

    return (
        <div className="mt-8 rounded-8 overflow-hidden border position-relative" style={{ height: '170px', width: '100%' }}>
            <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
            {!mapLoaded && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light text-secondary text-xs" style={{ zIndex: 1 }}>
                    <span className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                    Loading Map...
                </div>
            )}
        </div>
    );
};

const VenuePreviewModal = ({ show, onHide, venueId }) => {
    const { auth } = useAuth();
    const [venue, setVenue] = useState(null);
    const [menu, setMenu] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch Venue Details (v2 includes features, blogs, gallery)
                const venueRes = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}`);
                if (!venueRes.ok) throw new Error('Failed to fetch venue details');
                const venueData = await venueRes.json();
                setVenue(venueData);

                // Fetch Menu
                const menuRes = await auth.fetch(`${config.API_BASE_URL}/api/menus/venue/${venueId}`);
                if (menuRes.ok) {
                    const menuData = await menuRes.json();
                    setMenu(menuData);
                }
            } catch (err) {
                console.error('Error fetching preview data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (show && venueId) {
            fetchData();
        }
    }, [show, venueId, auth]);

    const getImageUrl = (path) => {
        if (!path) return '/assets/images/logo.png';
        if (path.startsWith('http')) return path;
        return `${config.API_BASE_URL}/${path.replace(/^\//, '')}`;
    };

    if (!show) return null;

    const getStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return <span className="badge bg-success-600 px-12 py-6 rounded-pill fw-semibold text-white d-inline-flex align-items-center gap-1"><Icon icon="solar:check-circle-bold" /> Approved</span>;
            case 'rejected':
                return <span className="badge bg-danger-600 px-12 py-6 rounded-pill fw-semibold text-white d-inline-flex align-items-center gap-1"><Icon icon="solar:close-circle-bold" /> Rejected</span>;
            default:
                return <span className="badge bg-warning-600 px-12 py-6 rounded-pill fw-semibold text-dark d-inline-flex align-items-center gap-1"><Icon icon="solar:clock-circle-bold" /> Pending Verification</span>;
        }
    };

    const getPriceTierLabel = (tier) => {
        switch (tier) {
            case '$':
                return 'Budget (Under 100 AED)';
            case '$$':
                return 'Moderate (100 – 250 AED)';
            case '$$$':
                return 'Fine Dining (250 – 500 AED)';
            case '$$$$':
                return 'Luxury / VIP (500+ AED)';
            default:
                return 'Luxury / VIP (500+ AED)';
        }
    };

    const getPriceBadge = (tier) => {
        switch (tier) {
            case '$': return 'Budget';
            case '$$': return 'Moderate';
            case '$$$': return 'Fine Dining';
            case '$$$$': return 'Luxury / VIP';
            default: return 'Luxury / VIP';
        }
    };

    const getEstimatedAvgPrice = (tier, val) => {
        if (val && Number(val) > 0) return `${Number(val).toFixed(0)} AED`;
        switch (tier) {
            case '$': return '75 – 150 AED';
            case '$$': return '150 – 300 AED';
            case '$$$': return '300 – 550 AED';
            case '$$$$': return '600+ AED';
            default: return '600+ AED';
        }
    };

    const getDmLink = (type, value) => {
        if (!type || !value) return null;
        if (type === 'whatsapp') {
            const cleanNum = value.replace(/[^\d+]/g, '');
            return `https://wa.me/${cleanNum.startsWith('+') ? cleanNum.slice(1) : cleanNum}`;
        }
        if (type === 'instagram') {
            const cleanUser = value.replace(/^@+/, '');
            return `https://instagram.com/${cleanUser}`;
        }
        return null;
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable className="venue-preview-modal">
            <Modal.Header closeButton className="border-bottom bg-white py-16 px-24">
                <div className="d-flex align-items-center gap-2">
                    <div className="w-36-px h-36-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                        <Icon icon="solar:eye-bold" className="text-xl" />
                    </div>
                    <div>
                        <Modal.Title className="text-lg fw-bold text-dark mb-0">Live Venue Preview</Modal.Title>
                        <span className="text-xs text-secondary-light">Customer & Public View Representation (All Prices in AED)</span>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="p-0 bg-light">
                {loading ? (
                    <div className="d-flex flex-column justify-content-center align-items-center p-5" style={{ minHeight: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading preview...</span>
                        </div>
                        <p className="text-secondary mt-3 fw-medium">Loading venue details & menu...</p>
                    </div>
                ) : error ? (
                    <div className="text-center text-danger p-5">
                        <Icon icon="solar:danger-circle-bold" className="text-5xl mb-2 text-danger-main" />
                        <h6 className="fw-bold text-danger">Failed to load preview</h6>
                        <p className="text-secondary text-sm">{error}</p>
                    </div>
                ) : venue ? (
                    <div className="bg-light">
                        {/* Hero Section with Enhanced Contrast & Badges */}
                        <div className="position-relative" style={{ height: '320px', backgroundColor: '#111827' }}>
                            <img 
                                src={getImageUrl(venue.thumbnail || (venue.gallery && venue.gallery[0]?.image_url))} 
                                alt={venue.title || venue.business_name}
                                className="w-100 h-100 object-fit-cover opacity-90"
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = '/assets/images/placeholder.png';
                                }}
                            />
                            
                            {/* Top Badges Bar */}
                            <div className="position-absolute top-0 start-0 w-100 p-3 d-flex justify-content-between align-items-center">
                                <div className="d-flex gap-2">
                                    {getStatusBadge(venue.verification_status)}
                                    {venue.category_name && (
                                        <span className="badge bg-dark bg-opacity-75 text-white px-12 py-6 rounded-pill backdrop-blur">
                                            <Icon icon="solar:tag-bold" className="me-1" />
                                            {venue.category_name}
                                        </span>
                                    )}
                                    <span className="badge bg-dark bg-opacity-75 text-warning px-12 py-6 rounded-pill backdrop-blur fw-bold">
                                        {getPriceBadge(venue.price_level)}
                                    </span>
                                </div>
                                {venue.license_number && (
                                    <span className="badge bg-dark bg-opacity-75 text-white px-12 py-6 rounded-pill backdrop-blur text-xs">
                                        License: {venue.license_number}
                                    </span>
                                )}
                            </div>

                            {/* Hero Bottom Overlay */}
                            <div 
                                className="position-absolute bottom-0 start-0 w-100 p-4 text-white"
                                style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(17,24,39,0.92) 80%, rgba(17,24,39,1) 100%)' }}
                            >
                                <div className="container-fluid px-3">
                                    <div className="d-flex align-items-end gap-3 flex-wrap">
                                        <div className="w-80-px h-80-px rounded-3 border border-2 border-white bg-primary text-white d-flex align-items-center justify-content-center shadow-sm fw-bold text-2xl flex-shrink-0 overflow-hidden position-relative">
                                            <span className="user-select-none">{(venue.title || venue.business_name || 'V').charAt(0).toUpperCase()}</span>
                                            {venue.logo && (
                                                <img 
                                                    src={getImageUrl(venue.logo)} 
                                                    alt="Logo"
                                                    className="position-absolute top-0 start-0 w-100 h-100 object-fit-cover bg-white"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = 'none';
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <div className="mb-1 flex-grow-1">
                                            <h2 className="h3 mb-1 fw-bold text-white text-shadow">{venue.title || venue.business_name}</h2>
                                            <p className="mb-0 text-white opacity-90 d-flex align-items-center gap-2 text-sm">
                                                <span><Icon icon="solar:map-point-bold" className="text-primary-400" /> {venue.city || 'Dubai'}, {venue.country || 'UAE'}</span>
                                                <span>• Avg. {getEstimatedAvgPrice(venue.price_level, venue.average_price_for_two)} for two</span>
                                            </p>
                                        </div>
                                        <div
                                            className="bg-white bg-opacity-90 text-danger-600 px-16 py-8 rounded-pill d-flex align-items-center gap-2 shadow fw-semibold text-sm"
                                            title="Number of end users who saved this venue as a favorite"
                                        >
                                            <Icon icon="solar:heart-bold" className="text-lg" />
                                            Saved by {Number(venue.favorite_count) || 0} users
                                        </div>
                                        {venue.dm_type && venue.dm_value && (
                                            <a 
                                                href={getDmLink(venue.dm_type, venue.dm_value)} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className={`btn btn-sm ${venue.dm_type === 'whatsapp' ? 'btn-success' : 'btn-danger'} px-16 py-8 rounded-pill d-flex align-items-center gap-1 shadow`}
                                            >
                                                <Icon icon={venue.dm_type === 'whatsapp' ? 'logos:whatsapp-icon' : 'skill-icons:instagram'} />
                                                Direct {venue.dm_type === 'whatsapp' ? 'WhatsApp' : 'Instagram'}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Content Container */}
                        <div className="container-fluid p-24">
                            <Tabs defaultActiveKey="overview" className="custom-tabs mb-20 border-bottom bg-white rounded-3 p-2 shadow-sm">
                                {/* Tab 1: Overview */}
                                <Tab eventKey="overview" title="Overview & Details">
                                    <Row className="g-4 mt-1">
                                        {/* Left Column: Description, Features, Entertainment */}
                                        <Col lg={8}>
                                            {/* About Card */}
                                            <Card className="border-0 shadow-sm mb-4 radius-12">
                                                <Card.Body className="p-20">
                                                    <h5 className="mb-3 fw-bold text-dark d-flex align-items-center gap-2">
                                                        <Icon icon="solar:document-text-bold" className="text-primary-600" />
                                                        About Venue
                                                    </h5>
                                                    <p className="text-dark mb-0 fw-normal" style={{ whiteSpace: 'pre-line', lineHeight: '1.7', fontSize: '0.95rem' }}>
                                                        {venue.description || 'No description provided.'}
                                                    </p>
                                                </Card.Body>
                                            </Card>

                                            {/* Entertainment & Events Card (If Available) */}
                                            {(venue.entertainment_type || venue.entertainment_name || venue.entertainment_description) && (
                                                <Card className="border-0 shadow-sm mb-4 radius-12 bg-primary-50 bg-opacity-25 border-start border-4 border-primary">
                                                    <Card.Body className="p-20">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <h5 className="fw-bold text-dark d-flex align-items-center gap-2 mb-0">
                                                                <Icon icon="solar:music-notes-bold" className="text-primary-600" />
                                                                Live Entertainment & Performances
                                                            </h5>
                                                            {venue.entertainment_type && (
                                                                <Badge bg="primary" className="px-12 py-6 rounded-pill">
                                                                    {venue.entertainment_type}
                                                                </Badge>
                                                            )}
                                                        </div>

                                                        {venue.entertainment_name && (
                                                            <h6 className="fw-bold text-primary-700 mt-2 mb-1">
                                                                {venue.entertainment_name}
                                                            </h6>
                                                        )}

                                                        {venue.entertainment_description && (
                                                            <p className="text-dark text-sm mb-2" style={{ lineHeight: '1.6' }}>
                                                                {venue.entertainment_description}
                                                            </p>
                                                        )}

                                                        <div className="d-flex flex-wrap gap-3 mt-3 pt-2 border-top">
                                                            {venue.entertainment_schedule && (
                                                                <div className="d-flex align-items-center gap-1 text-sm text-dark">
                                                                    <Icon icon="solar:calendar-bold" className="text-primary-600" />
                                                                    <strong className="text-secondary-light">Schedule:</strong> {venue.entertainment_schedule}
                                                                </div>
                                                            )}
                                                            {venue.entertainment_price > 0 && (
                                                                <div className="d-flex align-items-center gap-1 text-sm text-dark">
                                                                    <Icon icon="solar:ticket-bold" className="text-success-main" />
                                                                    <strong className="text-secondary-light">Entry Fee:</strong> {venue.entertainment_price} AED
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            )}

                                            {/* Features & Amenities */}
                                            {venue.features && venue.features.length > 0 && (
                                                <Card className="border-0 shadow-sm mb-4 radius-12">
                                                    <Card.Body className="p-20">
                                                        <h5 className="mb-3 fw-bold text-dark d-flex align-items-center gap-2">
                                                            <Icon icon="solar:stars-bold" className="text-warning-main" />
                                                            Features & Amenities
                                                        </h5>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {venue.features.map(feature => (
                                                                <div key={feature.id} className="d-flex align-items-center gap-2 px-14 py-8 bg-white rounded-pill border shadow-2xs">
                                                                    <Icon icon={feature.icon || 'solar:check-circle-bold'} className="text-primary-600" />
                                                                    <span className="fw-semibold text-dark text-sm">{feature.name}</span>
                                                                    {feature.price > 0 && (
                                                                        <span className="badge bg-primary-50 text-primary-700 px-8 py-2 rounded-pill text-xs fw-bold">
                                                                            {feature.price} AED
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            )}

                                            {/* Gallery Preview */}
                                            {venue.gallery && venue.gallery.length > 0 && (
                                                <Card className="border-0 shadow-sm radius-12">
                                                    <Card.Body className="p-20">
                                                        <h5 className="mb-3 fw-bold text-dark d-flex align-items-center gap-2">
                                                            <Icon icon="solar:gallery-bold" className="text-info-main" />
                                                            Gallery Photos ({venue.gallery.length})
                                                        </h5>
                                                        <Row className="g-2">
                                                            {venue.gallery.slice(0, 8).map((img, idx) => (
                                                                <Col xs={6} sm={4} md={3} key={img.id || idx}>
                                                                    <div className="ratio ratio-1x1 rounded-3 overflow-hidden border shadow-2xs">
                                                                        <img 
                                                                            src={getImageUrl(img.image_url)} 
                                                                            alt="" 
                                                                            className="w-100 h-100 object-fit-cover hover-scale transition-all"
                                                                            onError={(e) => {
                                                                                e.currentTarget.onerror = null;
                                                                                e.currentTarget.src = '/assets/images/placeholder.png';
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            )}
                                        </Col>
                                        
                                        {/* Right Column: Pricing, Contact Info, Location, Owner */}
                                        <Col lg={4}>
                                            {/* Pricing & Spend Card (Always in AED) */}
                                            <Card className="border-0 shadow-sm mb-4 radius-12">
                                                <Card.Body className="p-20">
                                                    <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                        <Icon icon="solar:wallet-money-bold" className="text-success-main" />
                                                        Pricing & Budget (AED)
                                                    </h6>
                                                    <div className="p-12 bg-light rounded-3 mb-2">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-secondary-light text-sm">Price Category:</span>
                                                            <span className="fw-bold text-dark text-md">{getPriceBadge(venue.price_level)}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-secondary-light text-sm">Typical Range:</span>
                                                            <span className="fw-semibold text-secondary-dark text-xs">{getPriceTierLabel(venue.price_level)}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-secondary-light text-sm">Avg. for Two:</span>
                                                            <span className="fw-bold text-primary-600 text-md">
                                                                {getEstimatedAvgPrice(venue.price_level, venue.average_price_for_two)}
                                                            </span>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                                            <span className="text-secondary-light text-xs">Official Currency:</span>
                                                            <span className="fw-bold text-success-main text-xs">
                                                                AED (UAE Dirham)
                                                            </span>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>

                                            {/* Contact & Instant Messaging Card */}
                                            <Card className="border-0 shadow-sm mb-4 radius-12">
                                                <Card.Body className="p-20">
                                                    <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                        <Icon icon="solar:phone-calling-bold" className="text-primary-600" />
                                                        Contact Details
                                                    </h6>
                                                    <ul className="list-unstyled d-flex flex-column gap-3 mb-0">
                                                        {venue.email && (
                                                            <li className="d-flex align-items-center gap-2">
                                                                <div className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                                                                    <Icon icon="solar:letter-bold" />
                                                                </div>
                                                                <span className="text-sm fw-medium text-dark text-break">{venue.email}</span>
                                                            </li>
                                                        )}
                                                        {venue.phone && (
                                                            <li className="d-flex align-items-center gap-2">
                                                                <div className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                                                                    <Icon icon="solar:phone-bold" />
                                                                </div>
                                                                <span className="text-sm fw-medium text-dark">{venue.phone}</span>
                                                            </li>
                                                        )}
                                                        {venue.website && (
                                                            <li className="d-flex align-items-center gap-2">
                                                                <div className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                                                                    <Icon icon="solar:globe-bold" />
                                                                </div>
                                                                <a href={venue.website} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 fw-medium text-truncate">
                                                                    {venue.website}
                                                                </a>
                                                            </li>
                                                        )}
                                                        {venue.dm_type && venue.dm_value && (
                                                            <li className="d-flex align-items-center gap-2">
                                                                <div className={`w-32-px h-32-px rounded-circle ${venue.dm_type === 'whatsapp' ? 'bg-success-50 text-success-main' : 'bg-danger-50 text-danger-main'} d-flex align-items-center justify-content-center`}>
                                                                    <Icon icon={venue.dm_type === 'whatsapp' ? 'logos:whatsapp-icon' : 'skill-icons:instagram'} />
                                                                </div>
                                                                <span className="text-sm fw-bold text-dark">
                                                                    {venue.dm_type === 'whatsapp' ? `WhatsApp: ${venue.dm_value}` : `@${venue.dm_value}`}
                                                                </span>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </Card.Body>
                                            </Card>

                                            {/* Location Details Card */}
                                            <Card className="border-0 shadow-sm mb-4 radius-12">
                                                <Card.Body className="p-20">
                                                    <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                        <Icon icon="solar:map-point-bold" className="text-danger-main" />
                                                        Location & Map
                                                    </h6>
                                                    <p className="text-sm text-dark mb-2 fw-medium">
                                                        {venue.address || `${venue.street || ''}, ${venue.city || ''}, ${venue.country || ''}`}
                                                    </p>
                                                    {venue.latitude && venue.longitude && (
                                                        <div className="mb-2">
                                                            <VenueMiniMap 
                                                                latitude={venue.latitude} 
                                                                longitude={venue.longitude} 
                                                                title={venue.title || venue.business_name} 
                                                            />
                                                            <div className="d-flex align-items-center justify-content-between mt-8 flex-wrap gap-2">
                                                                <span className="text-2xs text-secondary-light font-monospace">
                                                                    {Number(venue.latitude).toFixed(5)}, {Number(venue.longitude).toFixed(5)}
                                                                </span>
                                                                <div className="d-flex gap-1">
                                                                    <a 
                                                                        href={`https://www.google.com/maps/dir/?api=1&destination=${venue.latitude},${venue.longitude}`} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-2xs btn-outline-secondary radius-6 d-inline-flex align-items-center gap-1"
                                                                        style={{ fontSize: '11px', padding: '3px 8px' }}
                                                                    >
                                                                        <Icon icon="solar:routing-2-bold" /> Directions
                                                                    </a>
                                                                    <a 
                                                                        href={`https://www.google.com/maps?q=${venue.latitude},${venue.longitude}`} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-2xs btn-primary radius-6 d-inline-flex align-items-center gap-1"
                                                                        style={{ fontSize: '11px', padding: '3px 8px' }}
                                                                    >
                                                                        <Icon icon="solar:map-arrow-bold" /> Open in Maps
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Card.Body>
                                            </Card>

                                            {/* Venue Owner / Application Meta */}
                                            <Card className="border-0 shadow-sm radius-12 bg-light">
                                                <Card.Body className="p-20">
                                                    <h6 className="fw-bold text-dark mb-2 text-xs text-uppercase tracking-wider">Owner / Submitter</h6>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="w-36-px h-36-px rounded-circle bg-primary-100 text-primary-700 fw-bold d-flex align-items-center justify-content-center">
                                                            <Icon icon="solar:user-bold" />
                                                        </div>
                                                        <div>
                                                            <span className="fw-semibold text-dark text-sm d-block">{venue.owner_name || 'Business Owner'}</span>
                                                            <span className="text-xs text-secondary-light">{venue.owner_email || venue.email}</span>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Tab>

                                {/* Tab 2: Menu Preview */}
                                <Tab eventKey="menu" title={`Menu (${menu?.items?.length || 0})`}>
                                    <div className="row g-4 mt-1">
                                        <div className="col-lg-3">
                                            {/* Categories Sidebar */}
                                            <div className="card border-0 shadow-sm sticky-top radius-12" style={{ top: '20px', zIndex: 1 }}>
                                                <div className="card-header bg-white py-12 px-16 border-bottom">
                                                    <h6 className="mb-0 fw-bold text-dark">Menu Categories</h6>
                                                </div>
                                                <div className="card-body p-0">
                                                    <div className="list-group list-group-flush rounded">
                                                        {menu?.categories?.map(cat => (
                                                            <a 
                                                                href={`#cat-${cat.id}`} 
                                                                key={cat.id} 
                                                                className="list-group-item list-group-item-action border-0 px-16 py-10 fw-semibold text-dark d-flex justify-content-between align-items-center"
                                                            >
                                                                <span>{cat.name}</span>
                                                                <span className="badge bg-light text-dark rounded-pill">
                                                                    {menu.items.filter(i => i.category_id === cat.id).length}
                                                                </span>
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-lg-9">
                                            {menu?.categories?.map(cat => {
                                                const catItems = menu.items.filter(item => item.category_id === cat.id);
                                                if (catItems.length === 0) return null;
                                                
                                                return (
                                                    <div key={cat.id} id={`cat-${cat.id}`} className="mb-4">
                                                        <h5 className="fw-bold mb-3 text-dark border-bottom pb-2 d-flex align-items-center gap-2">
                                                            <Icon icon="solar:chef-hat-heart-bold" className="text-primary-600" />
                                                            {cat.name}
                                                        </h5>
                                                        <div className="row g-3">
                                                            {catItems.map(item => (
                                                                <div className="col-md-6" key={item.id}>
                                                                    <div className="card h-100 border-0 shadow-sm radius-12 hover-shadow transition-all bg-white">
                                                                        <div className="card-body p-16 d-flex gap-3">
                                                                            {item.image_url && (
                                                                                <img 
                                                                                    src={getImageUrl(item.image_url)} 
                                                                                    alt={item.name} 
                                                                                    className="w-70-px h-70-px rounded-3 object-fit-cover flex-shrink-0"
                                                                                    onError={(e) => {
                                                                                        e.currentTarget.onerror = null;
                                                                                        e.currentTarget.src = '/assets/images/placeholder.png';
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            <div className="flex-grow-1">
                                                                                <div className="d-flex justify-content-between align-items-start mb-1">
                                                                                    <h6 className="fw-bold mb-0 text-dark text-truncate-2">{item.name}</h6>
                                                                                    <span className="fw-bold text-primary-600 ms-2 text-nowrap">
                                                                                        {item.price} AED
                                                                                    </span>
                                                                                </div>
                                                                                <p className="text-xs text-secondary-light mb-0 line-clamp-2">{item.description}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {(!menu?.items || menu.items.length === 0) && (
                                                <div className="text-center py-5 text-secondary bg-white rounded-3 p-4 shadow-sm">
                                                    <Icon icon="solar:chef-hat-bold" className="text-5xl mb-2 text-secondary-light" />
                                                    <h6 className="fw-semibold text-dark">No menu items uploaded</h6>
                                                    <p className="text-sm text-secondary-light">This venue has not uploaded a digital menu yet.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Tab>

                                {/* Tab 3: Blogs & Posts */}
                                <Tab eventKey="blogs" title={`Blogs & Events (${venue.blogs?.length || 0})`}>
                                    <Row className="g-4 mt-1">
                                        {venue.blogs?.map(blog => (
                                            <Col md={6} lg={4} key={blog.id}>
                                                <Card className="h-100 border-0 shadow-sm radius-12 overflow-hidden bg-white">
                                                    <div className="ratio ratio-16x9">
                                                        <img 
                                                            src={getImageUrl(blog.image_url)} 
                                                            className="card-img-top object-fit-cover" 
                                                            alt={blog.title} 
                                                            onError={(e) => {
                                                                e.currentTarget.onerror = null;
                                                                e.currentTarget.src = '/assets/images/placeholder.png';
                                                            }}
                                                        />
                                                    </div>
                                                    <Card.Body className="p-16">
                                                        <div className="text-xs text-primary-600 fw-semibold mb-1">
                                                            {formatDisplayDate(blog.created_at)}
                                                        </div>
                                                        <h6 className="card-title fw-bold text-dark line-clamp-2 mb-2">{blog.title}</h6>
                                                        <p className="card-text text-sm text-secondary line-clamp-3 mb-0">
                                                            {blog.content?.replace(/<[^>]*>?/gm, '')}
                                                        </p>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                        {(!venue.blogs || venue.blogs.length === 0) && (
                                            <Col xs={12}>
                                                <div className="text-center py-5 text-secondary bg-white rounded-3 p-4 shadow-sm">
                                                    <Icon icon="solar:document-text-bold" className="text-5xl mb-2 text-secondary-light" />
                                                    <h6 className="fw-semibold text-dark">No blogs or events posted</h6>
                                                    <p className="text-sm text-secondary-light">This venue has not published any blog posts yet.</p>
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                </Tab>
                            </Tabs>
                        </div>
                    </div>
                ) : null}
            </Modal.Body>

            <Modal.Footer className="border-top bg-white py-12 px-24">
                <Button variant="secondary" className="px-20 radius-8" onClick={onHide}>Close Preview</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default VenuePreviewModal;
